import React, {
  createContext,
  useState,
  useCallback,
  useRef,
  useEffect,
} from "react";
import { Snackbar, Alert, Modal } from "@mui/material";
import Box from "@mui/material/Box";
import Fade from "@mui/material/Fade";
import Typography from "@mui/material/Typography";

import { ConfirmDialog } from "../_components/ConfirmDialog";

import "../css/style.css";
const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  minWidth: 250,
  maxWidth: 320,
  bgcolor: "background.paper",
  border: "2px solid #000",
  boxShadow: 24,
  p: 4,
};


// Создаем контекст
export const SnackbarContext = createContext();

// Провайдер для снекбара
export const SnackbarProvider = ({ children }) => {
  const [message, setMessage] = useState("");
  const [modalText, setModalText] = useState("");
  const [severity, setSeverity] = useState("info"); // Тип сообщения: 'success', 'error', 'warning', 'info'
  const [open, setOpen] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [confirmState, setConfirmState] = useState(null);
  const resolveRef = useRef(null);
  const resolveRefModal = useRef(null);

  // Функция для показа снекбара
  const showSnackbar = useCallback((text, type = "info") => {
    return new Promise((resolve) => {
      setMessage(text);
      setSeverity(type);
      setOpen(true);
      resolveRef.current = resolve; // Сохраняем resolve для закрытия
    });
  }, []);

  const showModal = useCallback((text, type = "info") => {
    return new Promise((resolve) => {
      setModalText(text);
      setOpenModal(true);
      resolveRef.current = resolve; // Сохраняем resolve для закрытия
    });
  }, []);

  useEffect(() => {
    initSnackbarService(showSnackbar);
  }, [showSnackbar]);

  useEffect(() => {
    initModalService(showModal);
  }, [showModal]);

  // Вопрос «точно удалить?» вместо window.confirm: системное окно WebView
  // подписывается адресом страницы («…http://localhost сообщает»), и убрать эту
  // строку нельзя. Промис резолвится ответом человека, поэтому вызывающий код
  // остаётся линейным: showGlobalConfirm(...).then((ok) => { if (!ok) return; ... }).
  const showConfirm = useCallback((text, confirmLabel) => {
    return new Promise((resolve) => {
      setConfirmState({ text, confirmLabel, resolve });
    });
  }, []);

  useEffect(() => {
    initConfirmService(showConfirm);
  }, [showConfirm]);

  const closeConfirm = useCallback((answer) => {
    setConfirmState((prev) => {
      if (prev) prev.resolve(answer);
      return null;
    });
  }, []);

  // Функция для закрытия снекбара
  const closeSnackbar = useCallback(() => {
    setOpen(false);
    if (resolveRef.current) {
      resolveRef.current(); // Вызываем resolve при закрытии
      resolveRef.current = null;
    }
  }, []);

  const closeModal = useCallback(() => {
    setOpenModal(false);
    if (resolveRefModal.current) {
      resolveRefModal.current(); // Вызываем resolve при закрытии
      resolveRefModal.current = null;
    }
  }, []);

  return (
    <SnackbarContext.Provider value={{ showSnackbar, showConfirm }}>
      {children}
      {/* Компонент Snackbar */}
      <Snackbar
        open={open}
        autoHideDuration={5000} // Автоматическое закрытие через 3 секунды
        onClose={closeSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={closeSnackbar}
          severity={severity}
          sx={{ width: "100%" }}
        >
          {message}
        </Alert>
      </Snackbar>
      <Modal
      aria-labelledby="transition-modal-title"
      aria-describedby="transition-modal-description"
      open={openModal}
      onClose={closeModal}
    >
      <Fade in={openModal}>
        <Box sx={style}>
          <Typography id="transition-modal-title" variant="h6" component="h2" style={{whiteSpace: 'pre-wrap' }}>
           {modalText}
          </Typography>
        </Box>
      </Fade>
    </Modal>
      <ConfirmDialog
        open={!!confirmState}
        text={confirmState ? confirmState.text : ""}
        confirmLabel={confirmState ? confirmState.confirmLabel : ""}
        onConfirm={() => closeConfirm(true)}
        onCancel={() => closeConfirm(false)}
      />
    </SnackbarContext.Provider>
  );
};

// Экспорт функции для вызова снекбара извне
let globalShowSnackbar;
let globalShowModal;
let globalShowConfirm;

export const initSnackbarService = (showSnackbar) => {
  globalShowSnackbar = showSnackbar;
};

export const initModalService = (showModal) => {
  globalShowModal = showModal;
};

export const initConfirmService = (showConfirm) => {
  globalShowConfirm = showConfirm;
};

export const showGlobalSnackbar = (text, type = "info") => {
  if (!globalShowSnackbar) {
    console.error("Snackbar service is not initialized");
    return;
  }
  globalShowSnackbar(text, type);
};

export const showGlobalModal = (text, type = "info") => {
  if (!globalShowModal) {
    console.error("Modal service is not initialized");
    return;
  }
  globalShowModal(text, type);
};

// Возвращает промис с ответом человека. Если провайдер почему-то не смонтирован,
// откатываемся на window.confirm — лучше системное окно, чем молча съеденное
// действие.
export const showGlobalConfirm = (text, confirmLabel) => {
  if (!globalShowConfirm) {
    console.error("Confirm service is not initialized");
    return Promise.resolve(window.confirm(text));
  }
  return globalShowConfirm(text, confirmLabel);
};
